import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { Suspense, type ReactNode } from 'react';
import RouterErrorPage from '@/components/feedback/RouterErrorPage';
import { lazyRetry } from '@/lib/lazy-retry';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { TenantLayout } from '@/layouts/TenantLayout';
import { AuthGuard } from '@/components/shell/AuthGuard';
import { Spinner } from '@/components/foundation/Spinner';
import NotFoundPage from '@/apps/public/pages/NotFoundPage';

const LoginPage = lazyRetry(() => import('@/apps/public/pages/LoginPage'));
const RegisterPage = lazyRetry(() => import('@/apps/public/pages/RegisterPage'));
const PricingPage = lazyRetry(() => import('@/apps/public/pages/PricingPage'));

const AdminHomePage = lazyRetry(() => import('@/apps/admin/pages/HomePage'));
const TenantsListPage = lazyRetry(() => import('@/apps/admin/pages/TenantsListPage'));
const TenantDetailLayout = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantDetailLayout'));
const TenantProfilePage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantProfilePage'));
const TenantSubscriptionPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantSubscriptionPage'));
const TenantChannelWebPage = lazyRetry(() => import('@/apps/admin/pages/tenants/channels/TenantChannelWebPage'));
const TenantChannelsLayout = lazyRetry(() => import('@/apps/admin/pages/tenants/channels/TenantChannelsLayout'));
const TenantChannelWhatsAppPage = lazyRetry(() => import('@/apps/admin/pages/tenants/channels/TenantChannelWhatsAppPage'));
const TenantChannelVoicePage = lazyRetry(() => import('@/apps/admin/pages/tenants/channels/TenantChannelVoicePage'));
const TenantAIPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantAIPage'));
const TenantKnowledgePage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantKnowledgePage'));
const TenantDocumentsPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantDocumentsPage'));
const TenantProgressPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantProgressPage'));
const AdminTenantIntegrationsPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantIntegrationsPage'));
const TenantWidgetPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantWidgetPage'));
const TenantUsersPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantUsersPage'));
const TenantTestBenchPage = lazyRetry(() => import('@/apps/admin/pages/tenants/TenantTestBenchPage'));
const PayNowPage = lazyRetry(() => import('@/apps/admin/pages/revenue/PayNowPage'));
const PlansPage = lazyRetry(() => import('@/apps/admin/pages/revenue/PlansPage'));
const QualityDashboardPage = lazyRetry(() => import('@/apps/admin/pages/ai-ops/QualityDashboardPage'));
const ReviewQueuePage = lazyRetry(() => import('@/apps/admin/pages/ai-ops/ReviewQueuePage'));
const PipelinePage = lazyRetry(() => import('@/apps/admin/pages/ai-ops/PipelinePage'));
const IndustryListPage = lazyRetry(() => import('@/apps/admin/pages/ai-ops/IndustryListPage'));
const IndustryConfigPage = lazyRetry(() => import('@/apps/admin/pages/ai-ops/IndustryConfigPage'));
const MonitoringLayout = lazyRetry(() => import('@/apps/admin/pages/monitoring/MonitoringLayout'));
const AdminActivityLogPage = lazyRetry(() => import('@/apps/admin/pages/monitoring/ActivityLogPage'));
const JobRunsPage = lazyRetry(() => import('@/apps/admin/pages/monitoring/JobRunsPage'));
const CrawlStatusPage = lazyRetry(() => import('@/apps/admin/pages/monitoring/CrawlStatusPage'));
const ChannelMonitoringPage = lazyRetry(() => import('@/apps/admin/pages/monitoring/ChannelMonitoringPage'));
const SystemLayout = lazyRetry(() => import('@/apps/admin/pages/system/SystemLayout'));
const SystemSettingsPage = lazyRetry(() => import('@/apps/admin/pages/system/SystemSettingsPage'));
const AdminAccountPage = lazyRetry(() => import('@/apps/admin/pages/system/AdminAccountPage'));
const AdminVoicePage = lazyRetry(() => import('@/apps/admin/pages/voice/VoicePlatformPage'));
const AdminIntegrationsPage = lazyRetry(() => import('@/apps/admin/pages/IntegrationsPlaceholderPage'));
const SecurityPlaceholderPage = lazyRetry(() => import('@/apps/admin/pages/SecurityPlaceholderPage'));

const TenantHomePage = lazyRetry(() => import('@/apps/tenant/pages/HomePage'));
const ChannelsLayout = lazyRetry(() => import('@/apps/tenant/pages/channels/ChannelsLayout'));
const WebChatChannelPage = lazyRetry(() => import('@/apps/tenant/pages/channels/WebChatChannelPage'));
const WhatsAppChannelPage = lazyRetry(() => import('@/apps/tenant/pages/channels/WhatsAppChannelPage'));
const VoiceChannelPage = lazyRetry(() => import('@/apps/tenant/pages/channels/VoiceChannelPage'));
const VoiceAgentSettingsPage = lazyRetry(() => import('@/apps/tenant/pages/channels/VoiceAgentSettingsPage'));
const SimulatorsLayout = lazyRetry(() => import('@/apps/tenant/pages/simulators/SimulatorsLayout'));
const WhatsAppSimulatorPage = lazyRetry(() => import('@/apps/tenant/pages/simulators/WhatsAppSimulatorPage'));
const VoiceSimulatorPage = lazyRetry(() => import('@/apps/tenant/pages/simulators/VoiceSimulatorPage'));
const VoiceRealtimePage = lazyRetry(() => import('@/apps/tenant/pages/simulators/VoiceRealtimePage'));
const InboxLayout = lazyRetry(() => import('@/apps/tenant/pages/inbox/InboxLayout'));
const ConversationsPage = lazyRetry(() => import('@/apps/tenant/pages/inbox/ConversationsPage'));
const InboxLeadsPage = lazyRetry(() => import('@/apps/tenant/pages/inbox/InboxLeadsPage'));
const CrmLayout = lazyRetry(() => import('@/apps/tenant/pages/crm/CrmLayout'));
const FollowUpsPage = lazyRetry(() => import('@/apps/tenant/pages/crm/FollowUpsPage'));
const AppointmentsPage = lazyRetry(() => import('@/apps/tenant/pages/crm/AppointmentsPage'));
const CrmLeadsPage = lazyRetry(() => import('@/apps/tenant/pages/crm/CrmLeadsPage'));
const ContactsPage = lazyRetry(() => import('@/apps/tenant/pages/crm/ContactsPage'));
const ContactDetailPage = lazyRetry(() => import('@/apps/tenant/pages/crm/ContactDetailPage'));
const TemplatesPage = lazyRetry(() => import('@/apps/tenant/pages/crm/TemplatesPage'));
const InsightsLayout = lazyRetry(() => import('@/apps/tenant/pages/insights/InsightsLayout'));
const BotHealthPage = lazyRetry(() => import('@/apps/tenant/pages/insights/BotHealthPage'));
const TenantActivityLogPage = lazyRetry(() => import('@/apps/tenant/pages/insights/ActivityLogPage'));
const AnalyticsPlaceholderPage = lazyRetry(() => import('@/apps/tenant/pages/insights/AnalyticsPlaceholderPage'));
const KnowledgeDocumentsPage = lazyRetry(() => import('@/apps/tenant/pages/KnowledgeDocumentsPage'));
const CalendarPage = lazyRetry(() => import('@/apps/tenant/pages/CalendarPage'));
const BillingPage = lazyRetry(() => import('@/apps/tenant/pages/BillingPage'));
const SettingsLayout = lazyRetry(() => import('@/apps/tenant/pages/settings/SettingsLayout'));
const BusinessProfilePage = lazyRetry(() => import('@/apps/tenant/pages/settings/BusinessProfilePage'));
const AiRagSettingsPage = lazyRetry(() => import('@/apps/tenant/pages/settings/AiRagSettingsPage'));
const NotificationsSettingsPage = lazyRetry(() => import('@/apps/tenant/pages/settings/NotificationsSettingsPage'));
const ApiKeysPage = lazyRetry(() => import('@/apps/tenant/pages/settings/ApiKeysPage'));
const TenantAccountPage = lazyRetry(() => import('@/apps/tenant/pages/settings/AccountPage'));
const TenantDealsPage = lazyRetry(() => import('@/apps/tenant/pages/crm/DealsPage'));
const TenantTestChannelPage = lazyRetry(() => import('@/apps/tenant/pages/channels/TestChannelPage'));
const WhatsAppFlowPage = lazyRetry(() => import('@/apps/tenant/pages/WhatsAppFlowPage'));
const TenantHelpCenterPage = lazyRetry(() => import('@/apps/tenant/pages/help/HelpCenterPage'));
const TenantIntegrationsPage = lazyRetry(() => import('@/apps/tenant/pages/IntegrationsPlaceholderPage'));
const AutomationPlaceholderPage = lazyRetry(() => import('@/apps/tenant/pages/AutomationPlaceholderPage'));

function S({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
}

const adminChildRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="home" replace /> },
  { path: 'home', element: <S><AdminHomePage /></S> },
  { path: 'tenants', element: <S><TenantsListPage /></S> },
  {
    path: 'tenants/:tenantId',
    element: <S><TenantDetailLayout /></S>,
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      { path: 'profile', element: <S><TenantProfilePage /></S> },
      { path: 'subscription', element: <S><TenantSubscriptionPage /></S> },
      {
        path: 'channels',
        element: <S><TenantChannelsLayout /></S>,
        children: [
          { index: true, element: <Navigate to="web" replace /> },
          { path: 'web', element: <S><TenantChannelWebPage /></S> },
          { path: 'whatsapp', element: <S><TenantChannelWhatsAppPage /></S> },
          { path: 'voice', element: <S><TenantChannelVoicePage /></S> },
        ],
      },
      { path: 'ai', element: <S><TenantAIPage /></S> },
      { path: 'knowledge', element: <S><TenantKnowledgePage /></S> },
      { path: 'documents', element: <S><TenantDocumentsPage /></S> },
      { path: 'progress', element: <S><TenantProgressPage /></S> },
      { path: 'integrations', element: <S><AdminTenantIntegrationsPage /></S> },
      { path: 'widget', element: <S><TenantWidgetPage /></S> },
      { path: 'users', element: <S><TenantUsersPage /></S> },
      { path: 'test-bench', element: <S><TenantTestBenchPage /></S> },
      { path: '*', element: <NotFoundPage portal="admin" /> },
    ],
  },
  { path: 'revenue', element: <Navigate to="/admin/revenue/paynow" replace /> },
  { path: 'revenue/paynow', element: <S><PayNowPage /></S> },
  { path: 'revenue/plans', element: <S><PlansPage /></S> },
  { path: 'ai-ops', element: <Navigate to="/admin/ai-ops/quality" replace /> },
  { path: 'ai-ops/quality', element: <S><QualityDashboardPage /></S> },
  { path: 'ai-ops/review-queue', element: <S><ReviewQueuePage /></S> },
  { path: 'ai-ops/pipeline', element: <S><PipelinePage /></S> },
  { path: 'ai-ops/industry', element: <S><IndustryListPage /></S> },
  { path: 'ai-ops/industry/:industryKey', element: <S><IndustryConfigPage /></S> },
  { path: 'ai-ops/voice', element: <S><AdminVoicePage /></S> },
  { path: 'voice', element: <Navigate to="/admin/ai-ops/voice" replace /> },
  {
    path: 'monitoring',
    element: <S><MonitoringLayout /></S>,
    children: [
      { index: true, element: <Navigate to="activity" replace /> },
      { path: 'activity', element: <S><AdminActivityLogPage /></S> },
      { path: 'jobs', element: <S><JobRunsPage /></S> },
      { path: 'crawl', element: <S><CrawlStatusPage /></S> },
      { path: 'channels', element: <S><ChannelMonitoringPage /></S> },
    ],
  },
  { path: 'integrations', element: <S><AdminIntegrationsPage /></S> },
  { path: 'security', element: <S><SecurityPlaceholderPage /></S> },
  {
    path: 'system',
    element: <S><SystemLayout /></S>,
    children: [
      { index: true, element: <Navigate to="settings" replace /> },
      { path: 'settings', element: <S><SystemSettingsPage /></S> },
      { path: 'account', element: <S><AdminAccountPage /></S> },
    ],
  },
  { path: '*', element: <NotFoundPage portal="admin" /> },
];

const tenantChildRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="home" replace /> },
  { path: 'dashboard', element: <Navigate to="home" replace /> },
  { path: 'home', element: <S><TenantHomePage /></S> },
  {
    path: 'channels',
    element: <S><ChannelsLayout /></S>,
    children: [
      { index: true, element: <Navigate to="web" replace /> },
      { path: 'web', element: <S><WebChatChannelPage /></S> },
      { path: 'whatsapp', element: <S><WhatsAppChannelPage /></S> },
      { path: 'voice', element: <S><VoiceChannelPage /></S> },
      { path: 'voice-agent', element: <S><VoiceAgentSettingsPage /></S> },
      { path: 'test', element: <S><TenantTestChannelPage /></S> },
    ],
  },
  {
    path: 'simulators',
    element: <S><SimulatorsLayout /></S>,
    children: [
      { index: true, element: <Navigate to="whatsapp" replace /> },
      { path: 'whatsapp', element: <S><WhatsAppSimulatorPage /></S> },
      { path: 'voice', element: <S><VoiceSimulatorPage /></S> },
      { path: 'voice-realtime', element: <S><VoiceRealtimePage /></S> },
    ],
  },
  {
    path: 'inbox',
    element: <S><InboxLayout /></S>,
    children: [
      { index: true, element: <Navigate to="conversations" replace /> },
      { path: 'conversations', element: <S><ConversationsPage /></S> },
      { path: 'conversations/:threadType/:threadId', element: <S><ConversationsPage /></S> },
      { path: 'leads', element: <S><InboxLeadsPage /></S> },
    ],
  },
  {
    path: 'crm',
    element: <S><CrmLayout /></S>,
    children: [
      { index: true, element: <Navigate to="follow-ups" replace /> },
      { path: 'follow-ups', element: <S><FollowUpsPage /></S> },
      { path: 'appointments', element: <S><AppointmentsPage /></S> },
      { path: 'leads', element: <S><CrmLeadsPage /></S> },
      { path: 'contacts', element: <S><ContactsPage /></S> },
      { path: 'contacts/:contactId', element: <S><ContactDetailPage /></S> },
      { path: 'deals', element: <S><TenantDealsPage /></S> },
      { path: 'templates', element: <S><TemplatesPage /></S> },
    ],
  },
  {
    path: 'insights',
    element: <S><InsightsLayout /></S>,
    children: [
      { index: true, element: <Navigate to="bot-health" replace /> },
      { path: 'bot-health', element: <S><BotHealthPage /></S> },
      { path: 'activity', element: <S><TenantActivityLogPage /></S> },
      { path: 'analytics', element: <S><AnalyticsPlaceholderPage /></S> },
    ],
  },
  { path: 'whatsapp-flow', element: <S><WhatsAppFlowPage /></S> },
  { path: 'help', element: <S><TenantHelpCenterPage /></S> },
  { path: 'help/:articleId', element: <S><TenantHelpCenterPage /></S> },
  { path: 'knowledge', element: <S><KnowledgeDocumentsPage /></S> },
  { path: 'calendar', element: <S><CalendarPage /></S> },
  { path: 'billing', element: <S><BillingPage /></S> },
  { path: 'integrations', element: <S><TenantIntegrationsPage /></S> },
  { path: 'automation', element: <S><AutomationPlaceholderPage /></S> },
  {
    path: 'settings',
    element: <S><SettingsLayout /></S>,
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      { path: 'profile', element: <S><BusinessProfilePage /></S> },
      { path: 'ai-rag', element: <S><AiRagSettingsPage /></S> },
      { path: 'notifications', element: <S><NotificationsSettingsPage /></S> },
      { path: 'api-keys', element: <S><ApiKeysPage /></S> },
      { path: 'account', element: <S><TenantAccountPage /></S> },
    ],
  },
  { path: '*', element: <NotFoundPage portal="tenant" /> },
];

export const appRouter = createBrowserRouter([
  { path: '/', element: <Navigate to="/public/login" replace /> },
  { path: '/tenant/login', element: <Navigate to="/public/login?portal=tenant" replace /> },
  {
    path: '/public',
    element: <PublicLayout />,
    errorElement: <RouterErrorPage />,
    children: [{ path: 'pricing', element: <S><PricingPage /></S> }],
  },
  {
    element: <AuthLayout />,
    errorElement: <RouterErrorPage />,
    children: [
      { path: '/public/login', element: <S><LoginPage /></S> },
      { path: '/public/register', element: <S><RegisterPage /></S> },
    ],
  },
  {
    path: '/admin',
    errorElement: <RouterErrorPage />,
    element: (
      <AuthGuard portal="admin">
        <AdminLayout />
      </AuthGuard>
    ),
    children: adminChildRoutes,
  },
  {
    path: '/tenant',
    errorElement: <RouterErrorPage />,
    element: (
      <AuthGuard portal="tenant">
        <TenantLayout />
      </AuthGuard>
    ),
    children: tenantChildRoutes,
  },
  { path: '*', element: <NotFoundPage /> },
]);
